import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useUserData } from '../context/UserDataContext';
import { useTheme } from '../context/ThemeContext';
import { TrendingUp, Lightbulb, Link, ChevronDown, ChevronUp, Users, Plus, X, Trash2 } from 'lucide-react';

const fmt = (n) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(n);
const fmtK = (n) => n >= 1000000 ? `$${(n / 1000000).toFixed(2)}M` : `$${(n / 1000).toFixed(0)}k`;

// ── Monte Carlo engine ────────────────────────────────────────────────────────
function boxMuller() {
  let u1 = 0, u2 = 0;
  while (u1 === 0) u1 = Math.random();
  while (u2 === 0) u2 = Math.random();
  return Math.sqrt(-2 * Math.log(u1)) * Math.cos(2 * Math.PI * u2);
}

function runMonteCarlo(p) {
  const {
    age, retirementAge, lifeExpectancy,
    currentSavings, monthlyContrib,
    retirementSpending, ssMonthly, ssAge,
    meanReturn, stdDev, inflation, numSims,
    // Spouse
    includeSpouse, spouseAge, spouseRetirementAge,
    spouseSavings, spouseContrib,
    spouseSsMonthly, spouseSsAge,
  } = p;

  const yearsToRetire = retirementAge - age;          // household retirement = primary's
  const yearsInRetire = lifeExpectancy - retirementAge;
  const totalYears = yearsToRetire + yearsInRetire;

  const paths = [];
  let successCount = 0;

  for (let sim = 0; sim < numSims; sim++) {
    // Pool both portfolios from day 1
    let portfolio = currentSavings + (includeSpouse ? spouseSavings : 0);
    const path = [portfolio];

    // ── Accumulation phase ──
    for (let y = 0; y < yearsToRetire; y++) {
      const r = meanReturn / 100 + boxMuller() * (stdDev / 100);

      // Spouse still contributing if they haven't hit their retirement age yet
      const spouseCurrentAge = spouseAge + y;
      const spouseWorking = includeSpouse && spouseCurrentAge < spouseRetirementAge;
      const totalContrib = monthlyContrib + (spouseWorking ? spouseContrib : 0);

      portfolio = portfolio * (1 + r) + totalContrib * 12;
      path.push(Math.max(portfolio, 0));
    }

    // ── Distribution phase ──
    // Spouse's age when the primary retires
    const spouseAgeAtRetirement = spouseAge + yearsToRetire;

    let depleted = false;
    for (let y = 0; y < yearsInRetire; y++) {
      const r = (meanReturn * 0.75) / 100 + boxMuller() * (stdDev / 100);
      const inflAdj = Math.pow(1 + inflation / 100, y);
      const spending = retirementSpending * 12 * inflAdj;

      // Primary SS — years after household retirement
      const primarySSDelay = ssAge - retirementAge;
      const primarySS = y >= primarySSDelay ? ssMonthly * 12 : 0;

      // Spouse SS — indexed by how old spouse is in year y of retirement
      let spouseSS = 0;
      if (includeSpouse) {
        const spouseCurrentAgeInRetirement = spouseAgeAtRetirement + y;
        spouseSS = spouseCurrentAgeInRetirement >= spouseSsAge ? spouseSsMonthly * 12 : 0;
      }

      portfolio = portfolio * (1 + r) - spending + primarySS + spouseSS;

      if (portfolio <= 0) {
        portfolio = 0;
        depleted = true;
      }
      path.push(portfolio);
    }

    if (!depleted) successCount++;
    paths.push({ path, survived: !depleted });
  }

  const finalValues = paths.map(p => p.path[totalYears]);
  finalValues.sort((a, b) => a - b);

  return {
    successRate: Math.round((successCount / numSims) * 100),
    paths,
    percentiles: {
      p10: finalValues[Math.floor(numSims * 0.10)],
      p25: finalValues[Math.floor(numSims * 0.25)],
      p50: finalValues[Math.floor(numSims * 0.50)],
      p75: finalValues[Math.floor(numSims * 0.75)],
      p90: finalValues[Math.floor(numSims * 0.90)],
    },
    totalYears,
    yearsToRetire,
  };
}

// ── Canvas chart ──────────────────────────────────────────────────────────────
function MonteCarloCanvas({ result, params }) {
  const canvasRef = useRef(null);
  const { theme } = useTheme();
  const isLight = theme === 'light';

  useEffect(() => {
    if (!result || !canvasRef.current) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const W = canvas.width;
    const H = canvas.height;
    const PAD = { top: 15, right: 15, bottom: 35, left: 60 };
    const plotW = W - PAD.left - PAD.right;
    const plotH = H - PAD.top - PAD.bottom;

    // Theme-aware palette
    const C = isLight
      ? { bg: '#f8fafc', grid: '#e2e8f0', retireLine: '#cbd5e1', label: '#64748b',
          survived: 'rgba(5, 150, 105, 0.18)', depleted: 'rgba(220, 38, 38, 0.18)',
          median: '#2563eb' }
      : { bg: '#0f172a', grid: '#1e293b', retireLine: '#334155', label: '#64748b',
          survived: 'rgba(16, 185, 129, 0.15)', depleted: 'rgba(239, 68, 68, 0.15)',
          median: '#60a5fa' };

    ctx.clearRect(0, 0, W, H);
    ctx.fillStyle = C.bg;
    ctx.fillRect(0, 0, W, H);

    // Use a loop instead of spread to avoid call-stack overflow with 5,000 paths
    let maxVal = 0;
    result.paths.forEach(({ path }) => {
      path.forEach(v => { if (v > maxVal) maxVal = v; });
    });
    maxVal *= 1.05;

    const xScale = (year) => PAD.left + (year / result.totalYears) * plotW;
    const yScale = (val) => PAD.top + plotH - (val / maxVal) * plotH;

    // Grid
    ctx.strokeStyle = C.grid;
    ctx.lineWidth = 1;
    for (let i = 0; i <= 5; i++) {
      const y = PAD.top + (i / 5) * plotH;
      ctx.beginPath(); ctx.moveTo(PAD.left, y); ctx.lineTo(PAD.left + plotW, y); ctx.stroke();
    }

    // Retirement line
    ctx.strokeStyle = C.retireLine;
    ctx.lineWidth = 1;
    ctx.setLineDash([4, 4]);
    const retX = xScale(result.yearsToRetire);
    ctx.beginPath(); ctx.moveTo(retX, PAD.top); ctx.lineTo(retX, PAD.top + plotH); ctx.stroke();
    ctx.setLineDash([]);
    ctx.fillStyle = C.label;
    ctx.font = '10px system-ui';
    ctx.fillText('Retire', retX + 3, PAD.top + 12);

    // Paths (200 max)
    result.paths.slice(0, 200).forEach(({ path, survived }) => {
      ctx.beginPath();
      ctx.lineWidth = 0.4;
      ctx.strokeStyle = survived ? C.survived : C.depleted;
      path.forEach((val, year) => {
        const x = xScale(year);
        const y = yScale(val);
        year === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y);
      });
      ctx.stroke();
    });

    // Median path
    const sorted = [...result.paths].sort((a, b) => b.path[result.totalYears] - a.path[result.totalYears]);
    const median = sorted[Math.floor(sorted.length / 2)];
    if (median) {
      ctx.beginPath();
      ctx.lineWidth = 2.5;
      ctx.strokeStyle = C.median;
      median.path.forEach((val, year) => {
        const x = xScale(year);
        const y = yScale(val);
        year === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y);
      });
      ctx.stroke();
    }

    // Y axis
    ctx.fillStyle = C.label;
    ctx.font = '10px system-ui';
    ctx.textAlign = 'right';
    for (let i = 0; i <= 4; i++) {
      const val = (maxVal * (4 - i)) / 4;
      const y = PAD.top + (i / 4) * plotH;
      ctx.fillText(fmtK(val), PAD.left - 5, y + 4);
    }

    // X axis
    ctx.textAlign = 'center';
    [0, 0.25, 0.5, 0.75, 1].forEach(pct => {
      const year = Math.floor(result.totalYears * pct);
      ctx.fillText(`Age ${params.age + year}`, xScale(year), PAD.top + plotH + 20);
    });

  }, [result, params, isLight]);

  return (
    <canvas
      ref={canvasRef}
      width={700}
      height={280}
      style={{ width: '100%', height: 280, borderRadius: 8 }}
    />
  );
}

// ── Slider ────────────────────────────────────────────────────────────────────
function Slider({ label, value, min, max, step, onChange, format, fromAccounts, accent }) {
  return (
    <div className="mb-3.5">
      <div className="flex justify-between items-center mb-1">
        <div className="flex items-center gap-2">
          <span className="text-xs font-medium text-slate-300">{label}</span>
          {fromAccounts && (
            <span className="text-xs bg-emerald-900/50 text-emerald-400 border border-emerald-700/40 rounded-full px-1.5 py-0.5 flex items-center gap-1">
              <Link size={9} /> from accounts
            </span>
          )}
        </div>
        <span className="text-xs font-bold text-slate-200">{format ? format(value) : value}</span>
      </div>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={e => onChange(Number(e.target.value))}
        className={`w-full h-1.5 bg-slate-700 rounded-full appearance-none cursor-pointer ${accent === 'purple' ? 'accent-purple-500' : 'accent-blue-500'}`}
      />
    </div>
  );
}

// ── Presets ───────────────────────────────────────────────────────────────────
const PRESETS = {
  conservative: { meanReturn: 5.5, stdDev: 10, inflation: 3.5 },
  moderate:     { meanReturn: 7.0, stdDev: 14, inflation: 3.0 },
  aggressive:   { meanReturn: 9.0, stdDev: 18, inflation: 2.5 },
  historical:   { meanReturn: 10.5, stdDev: 16, inflation: 3.0 },
};

// ── Add Account Modal ─────────────────────────────────────────────────────────
function AddAccountModal({ onClose, onAdd }) {
  const [name, setName] = useState('');
  const [balance, setBalance] = useState('');
  const [contribution, setContribution] = useState('');
  const [employerMatch, setEmployerMatch] = useState('');
  const [type, setType] = useState('401k');

  const handleSubmit = (e) => {
    e.preventDefault();
    const b = parseFloat(balance) || 0;
    const c = parseFloat(contribution) || 0;
    const m = parseFloat(employerMatch) || 0;
    if (!name.trim()) return;
    onAdd({ id: crypto.randomUUID(), name: name.trim(), type, balance: b, contribution: c, employerMatch: m });
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-slate-800 border border-slate-700 rounded-2xl w-full max-w-sm p-6" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-5">
          <h3 className="font-bold text-slate-100 text-lg">Add Retirement Account</h3>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-slate-700 transition-colors">
            <X size={16} className="text-slate-400" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="text-xs text-slate-400 mb-1.5 block">Account Name</label>
            <input value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Fidelity 401k" className="w-full bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 text-slate-100 text-sm focus:outline-none focus:border-blue-500" autoFocus required />
          </div>
          <div>
            <label className="text-xs text-slate-400 mb-1.5 block">Account Type</label>
            <select value={type} onChange={e => setType(e.target.value)} className="w-full bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 text-slate-100 text-sm focus:outline-none focus:border-blue-500">
              <option value="401k">401(k)</option>
              <option value="roth">Roth IRA</option>
              <option value="ira">Traditional IRA</option>
              <option value="hsa">HSA</option>
              <option value="other">Other</option>
            </select>
          </div>
          <div>
            <label className="text-xs text-slate-400 mb-1.5 block">Current Balance</label>
            <div className="flex items-center gap-2">
              <span className="text-slate-400 text-sm">$</span>
              <input value={balance} onChange={e => setBalance(e.target.value)} placeholder="0" type="number" min="0" className="w-full bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 text-slate-100 text-sm focus:outline-none focus:border-blue-500" required />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-slate-400 mb-1.5 block">Your Contrib/mo</label>
              <div className="flex items-center gap-1">
                <span className="text-slate-400 text-sm">$</span>
                <input value={contribution} onChange={e => setContribution(e.target.value)} placeholder="0" type="number" min="0" className="w-full bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 text-slate-100 text-sm focus:outline-none focus:border-blue-500" />
              </div>
            </div>
            <div>
              <label className="text-xs text-slate-400 mb-1.5 block">Employer Match/mo</label>
              <div className="flex items-center gap-1">
                <span className="text-slate-400 text-sm">$</span>
                <input value={employerMatch} onChange={e => setEmployerMatch(e.target.value)} placeholder="0" type="number" min="0" className="w-full bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 text-slate-100 text-sm focus:outline-none focus:border-blue-500" />
              </div>
            </div>
          </div>
          <div className="flex gap-3 pt-1">
            <button type="submit" className="flex-1 bg-blue-600 hover:bg-blue-500 text-white text-sm font-semibold py-2.5 rounded-lg transition-colors">Add Account</button>
            <button type="button" onClick={onClose} className="px-4 bg-slate-700 hover:bg-slate-600 text-slate-300 text-sm py-2.5 rounded-lg transition-colors">Cancel</button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Account breakdown sub-view ────────────────────────────────────────────────
function AccountBreakdown({ accounts, plaidAccounts, tips, onAddAccount, onDeleteAccount }) {
  const [showAdd, setShowAdd] = useState(false);
  const allAccounts = [...(plaidAccounts || []), ...accounts];
  const total = allAccounts.reduce((s, a) => s + (a.balance || 0), 0);

  const typeColor = { '401k': 'bg-blue-500', 'roth': 'bg-purple-500', 'roth_ira': 'bg-purple-500', 'hsa': 'bg-emerald-500', 'ira': 'bg-cyan-500' };
  const typeLabel = { '401k': '401(k)', roth_ira: 'Roth IRA', ira: 'IRA', roth: 'Roth IRA', hsa: 'HSA', other: 'Other' };

  return (
    <div className="space-y-4">
      {showAdd && (
        <AddAccountModal
          onClose={() => setShowAdd(false)}
          onAdd={(acct) => { onAddAccount(acct); setShowAdd(false); }}
        />
      )}

      <div className="flex justify-end">
        <button
          onClick={() => setShowAdd(true)}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white text-sm font-semibold rounded-lg transition-colors"
        >
          <Plus size={15} /> Add Account
        </button>
      </div>

      {allAccounts.length === 0 ? (
        <div className="bg-slate-800 border border-slate-700 rounded-xl p-10 text-center">
          <p className="text-slate-400 mb-1 font-medium">No retirement accounts yet</p>
          <p className="text-slate-500 text-sm mb-4">Add your 401k, Roth IRA, HSA, and other retirement accounts.</p>
          <button onClick={() => setShowAdd(true)} className="text-blue-400 hover:text-blue-300 text-sm">Add your first account →</button>
        </div>
      ) : (
        <>
          {/* Plaid-linked accounts (read-only) */}
          {(plaidAccounts || []).map((acct) => {
            const pct = total > 0 ? Math.round(((acct.balance || 0) / total) * 100) : 0;
            return (
              <div key={acct.id} className="bg-slate-800 border border-slate-700 rounded-xl p-5">
                <div className="flex justify-between items-start mb-3">
                  <div>
                    <div className="flex items-center gap-2">
                      <div className="font-semibold text-slate-200">{acct.name}</div>
                      <span className="text-xs bg-emerald-900/50 text-emerald-400 border border-emerald-700/40 rounded-full px-1.5 py-0.5 flex items-center gap-1">
                        <Link size={9} /> Plaid
                      </span>
                    </div>
                    <div className="text-xs text-slate-500 mt-0.5">{typeLabel[acct.type] || acct.type} · {acct.institution || 'Linked'}</div>
                  </div>
                  <div className="text-right">
                    <div className="text-xl font-bold text-slate-100">{fmt(acct.balance || 0)}</div>
                    <div className="text-xs text-slate-500">{pct}% of retirement</div>
                  </div>
                </div>
                <div className="w-full bg-slate-700 rounded-full h-1.5">
                  <div className={`h-1.5 rounded-full ${typeColor[acct.type] || 'bg-blue-500'}`} style={{ width: `${pct}%` }} />
                </div>
              </div>
            );
          })}
          {/* Manually entered accounts */}
          {accounts.map((acct, idx) => {
            const pct = total > 0 ? Math.round(((acct.balance || 0) / total) * 100) : 0;
            return (
              <div key={acct.id || acct.name} className="bg-slate-800 border border-slate-700 rounded-xl p-5 group">
                <div className="flex justify-between items-start mb-3">
                  <div>
                    <div className="font-semibold text-slate-200">{acct.name}</div>
                    <div className="text-xs text-slate-500 mt-0.5">
                      You: {fmt(acct.contribution)}/mo
                      {acct.employerMatch > 0 && ` + ${fmt(acct.employerMatch)}/mo employer match`}
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="text-right">
                      <div className="text-xl font-bold text-slate-100">{fmt(acct.balance)}</div>
                      <div className="text-xs text-slate-500">{pct}% of retirement</div>
                    </div>
                    <button
                      onClick={() => onDeleteAccount(idx)}
                      className="opacity-0 group-hover:opacity-100 p-1.5 rounded-lg hover:bg-rose-900/40 transition-all"
                    >
                      <Trash2 size={13} className="text-rose-400" />
                    </button>
                  </div>
                </div>
                <div className="w-full bg-slate-700 rounded-full h-1.5">
                  <div className={`h-1.5 rounded-full ${typeColor[acct.type] || 'bg-blue-500'}`} style={{ width: `${pct}%` }} />
                </div>
              </div>
            );
          })}
        </>
      )}

      {(tips || []).length > 0 && (
        <div className="bg-slate-800 border border-slate-700 rounded-xl p-5">
          <div className="flex items-center gap-2 mb-4">
            <Lightbulb size={16} className="text-amber-400" />
            <h3 className="font-semibold text-slate-200">Optimization Tips</h3>
          </div>
          <div className="space-y-3">
            {(tips || []).map((tip, i) => (
              <div key={i} className="flex items-start gap-3 p-3 bg-slate-700/40 rounded-lg">
                <span className="text-emerald-400 font-bold text-sm min-w-[55px]">{tip.impact}</span>
                <div>
                  <div className="text-sm font-medium text-slate-200">{tip.action}</div>
                  <div className="text-xs text-slate-400 mt-0.5">{tip.detail}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Helpers ───────────────────────────────────────────────────────────────────
function successColor(rate) {
  if (rate == null) return 'text-slate-500';
  return rate >= 90 ? 'text-emerald-400' : rate >= 75 ? 'text-amber-400' : 'text-rose-400';
}
function successBg(rate) {
  if (rate == null) return 'bg-slate-700';
  return rate >= 90 ? 'bg-emerald-500' : rate >= 75 ? 'bg-amber-500' : 'bg-rose-500';
}

// ── Main ──────────────────────────────────────────────────────────────────────
export default function Retirement() {
  const { retirementData, userProfile, updateSection, linkedAccountsData } = useUserData();

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const accounts = retirementData?.accounts || [];
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const tips     = retirementData?.tips     || [];

  // Plaid-linked retirement accounts (401k, Roth IRA, IRA)
  const plaidRetirementAccounts = (linkedAccountsData || []).filter(
    a => ['401k', 'roth_ira', 'ira'].includes(a.type) && a.connectionType === 'plaid'
  );
  const plaidRetirementBalance = plaidRetirementAccounts.reduce((s, a) => s + (a.balance || 0), 0);

  const computedSavings = accounts.reduce((s, a) => s + (a.balance || 0), 0);
  const computedContrib = accounts.reduce((s, a) => s + (a.contribution || 0) + (a.employerMatch || 0), 0);

  const [subView, setSubView] = useState('monte');
  const [preset, setPreset] = useState('moderate');
  const [spouseOpen, setSpouseOpen] = useState(false);
  const [params, setParams] = useState(() => ({
    age:              userProfile?.age            || 35,
    retirementAge:    userProfile?.retirementAge  || 65,
    lifeExpectancy:   userProfile?.lifeExpectancy || 90,
    currentSavings:   computedSavings,
    monthlyContrib:   computedContrib,
    retirementSpending: 7500,
    ssMonthly:        userProfile?.ssEstimate     || 0,
    ssAge: 67,
    ...PRESETS.moderate,
    numSims: 500,
    includeSpouse: false,
    spouseAge: 33,
    spouseRetirementAge: 63,
    spouseSavings: 0,
    spouseContrib: 0,
    spouseSsMonthly: 0,
    spouseSsAge: 67,
  }));

  // Seed currentSavings from Plaid once when live data arrives
  const plaidSeedRef = useRef(false);
  useEffect(() => {
    if (plaidSeedRef.current) return;
    const total = plaidRetirementBalance + computedSavings;
    if (total === 0) return;
    plaidSeedRef.current = true;
    setParams(p => ({ ...p, currentSavings: total }));
  }, [plaidRetirementBalance, computedSavings]); // eslint-disable-line react-hooks/exhaustive-deps
  const [result, setResult]           = useState(null);
  const [running, setRunning]         = useState(false);
  // Multi-run state
  const [multiResults, setMultiResults] = useState(null); // { r500, r1000, r5000 }
  const [multiRunning, setMultiRunning] = useState(null); // '500' | '1000' | '5000' | null
  const [canvasView, setCanvasView]     = useState('single'); // 'single'|'500'|'1000'|'5000'

  const set = (k) => (v) => setParams(p => ({ ...p, [k]: v }));

  const runSim = useCallback(() => {
    setRunning(true);
    setTimeout(() => {
      const newResult = runMonteCarlo(params);
      setResult(newResult);
      setRunning(false);
      // Persist the success rate so Overview KPI stays up to date
      updateSection('retirement', {
        ...(retirementData || {}),
        accounts,
        tips,
        successRate: newResult.successRate,
      });
    }, 50);
  }, [params, retirementData, accounts, tips, updateSection]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { runSim(); }, []);

  // Sequential multi-run: 500 → 1000 → 5000
  const runMulti = useCallback(() => {
    setMultiResults({ r500: null, r1000: null, r5000: null });
    setMultiRunning('500');
    setTimeout(() => {
      const r500 = runMonteCarlo({ ...params, numSims: 500 });
      setMultiResults(prev => ({ ...prev, r500 }));
      setMultiRunning('1000');
      setTimeout(() => {
        const r1000 = runMonteCarlo({ ...params, numSims: 1000 });
        setMultiResults(prev => ({ ...prev, r1000 }));
        setMultiRunning('5000');
        setTimeout(() => {
          const r5000 = runMonteCarlo({ ...params, numSims: 5000 });
          setMultiResults(prev => ({ ...prev, r5000 }));
          setMultiRunning(null);
          setCanvasView('5000');
        }, 100);
      }, 100);
    }, 50);
  }, [params]);

  const applyPreset = (name) => {
    setPreset(name);
    setParams(p => ({ ...p, ...PRESETS[name] }));
  };

  const toggleSpouse = () => {
    const next = !params.includeSpouse;
    setParams(p => ({ ...p, includeSpouse: next }));
    setSpouseOpen(next);
  };

  // successColor / successBg are module-level helpers above

  // Combined household totals for summary strip
  const combinedSavings = params.currentSavings + (params.includeSpouse ? params.spouseSavings : 0);
  const combinedContrib = params.monthlyContrib + (params.includeSpouse ? params.spouseContrib : 0);
  const combinedSS = params.ssMonthly + (params.includeSpouse ? params.spouseSsMonthly : 0);

  return (
    <div className="p-6 space-y-5 max-w-6xl mx-auto">
      <div>
        <h1 className="text-2xl font-bold text-slate-100">Retirement</h1>
        <p className="text-slate-400 text-sm mt-0.5">Monte Carlo planner + account breakdown</p>
      </div>

      {/* Sub-tabs */}
      <div className="flex gap-2">
        {[{ id: 'monte', label: 'Monte Carlo Planner' }, { id: 'accounts', label: 'Account Breakdown' }].map(t => (
          <button key={t.id} onClick={() => setSubView(t.id)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
              subView === t.id ? 'bg-blue-600 text-white' : 'bg-slate-800 text-slate-400 border border-slate-700 hover:text-slate-200'
            }`}>
            {t.label}
          </button>
        ))}
      </div>

      {subView === 'accounts' && (
        <AccountBreakdown
          accounts={accounts}
          plaidAccounts={plaidRetirementAccounts}
          tips={tips}
          onAddAccount={(acct) => {
            const newAccounts = [...accounts, acct];
            updateSection('retirement', { ...(retirementData || {}), accounts: newAccounts, tips });
          }}
          onDeleteAccount={(idx) => {
            const newAccounts = accounts.filter((_, i) => i !== idx);
            updateSection('retirement', { ...(retirementData || {}), accounts: newAccounts, tips });
          }}
        />
      )}

      {subView === 'monte' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

          {/* ── Controls panel ── */}
          <div className="bg-slate-800 border border-slate-700 rounded-xl p-5 overflow-y-auto max-h-[calc(100vh-200px)]">

            {/* Connected badge / Plaid sync */}
            {plaidRetirementBalance > 0 ? (
              <div className="mb-4 p-2.5 bg-emerald-900/30 border border-emerald-700/30 rounded-lg">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 bg-emerald-400 rounded-full animate-pulse" />
                    <span className="text-xs text-emerald-400 font-medium">Plaid · {fmt(plaidRetirementBalance)}</span>
                  </div>
                  <button
                    onClick={() => setParams(p => ({ ...p, currentSavings: plaidRetirementBalance + computedSavings }))}
                    className="text-xs text-emerald-400 hover:text-emerald-300 underline"
                  >
                    Reload
                  </button>
                </div>
                <div className="text-xs text-emerald-600 mt-0.5">
                  {plaidRetirementAccounts.length} retirement account{plaidRetirementAccounts.length !== 1 ? 's' : ''} linked
                </div>
              </div>
            ) : (
              <div className="flex items-center gap-2 mb-4 p-2 bg-emerald-900/30 border border-emerald-700/30 rounded-lg">
                <div className="w-2 h-2 bg-emerald-400 rounded-full animate-pulse" />
                <span className="text-xs text-emerald-400 font-medium">Connected to Finovo</span>
              </div>
            )}

            {/* Presets */}
            <div className="mb-4">
              <div className="text-xs text-slate-400 mb-2 font-medium">Scenario Preset</div>
              <div className="grid grid-cols-2 gap-1.5">
                {Object.keys(PRESETS).map(p => (
                  <button key={p} onClick={() => applyPreset(p)}
                    className={`py-1.5 px-2 rounded-lg text-xs font-medium capitalize transition-all ${
                      preset === p ? 'bg-blue-600 text-white' : 'bg-slate-700 text-slate-400 hover:text-slate-200'
                    }`}>
                    {p}
                  </button>
                ))}
              </div>
            </div>

            {/* ── You section ── */}
            <div className="border-t border-slate-700 pt-4">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-5 h-5 rounded-full bg-blue-600 flex items-center justify-center text-white text-xs font-bold">Y</div>
                <span className="text-xs font-semibold text-blue-400 uppercase tracking-wider">You</span>
              </div>
              <Slider label="Current Age" value={params.age} min={22} max={70} step={1} onChange={set('age')} />
              <Slider label="Retirement Age" value={params.retirementAge} min={50} max={75} step={1} onChange={set('retirementAge')} />
              <Slider label="Life Expectancy" value={params.lifeExpectancy} min={75} max={100} step={1} onChange={set('lifeExpectancy')} />
              <Slider label="Current Savings" value={params.currentSavings} min={0} max={2000000} step={5000} onChange={set('currentSavings')} format={fmt} fromAccounts />
              <Slider label="Monthly Contribution" value={params.monthlyContrib} min={0} max={10000} step={50} onChange={set('monthlyContrib')} format={fmt} fromAccounts />
              <Slider label="Retirement Spending/mo" value={params.retirementSpending} min={1000} max={20000} step={250} onChange={set('retirementSpending')} format={fmt} />
              <Slider label="Social Security/mo" value={params.ssMonthly} min={0} max={5000} step={50} onChange={set('ssMonthly')} format={fmt} fromAccounts />
              <Slider label="SS Claim Age" value={params.ssAge} min={62} max={70} step={1} onChange={set('ssAge')} />
            </div>

            {/* ── Spouse toggle ── */}
            <div className="mt-4 border-t border-slate-700 pt-4">
              <button
                onClick={toggleSpouse}
                className={`w-full flex items-center justify-between px-3 py-2.5 rounded-lg border transition-all ${
                  params.includeSpouse
                    ? 'bg-purple-900/40 border-purple-600/50 text-purple-300'
                    : 'bg-slate-700/40 border-slate-600/50 text-slate-400 hover:text-slate-200'
                }`}
              >
                <div className="flex items-center gap-2">
                  <Users size={14} className={params.includeSpouse ? 'text-purple-400' : 'text-slate-500'} />
                  <span className="text-xs font-semibold uppercase tracking-wider">
                    {params.includeSpouse ? 'Spouse Included' : 'Add Spouse'}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  {/* Toggle pill */}
                  <div className={`w-8 h-4 rounded-full transition-all relative ${params.includeSpouse ? 'bg-purple-500' : 'bg-slate-600'}`}>
                    <div className={`absolute top-0.5 w-3 h-3 rounded-full bg-white shadow transition-all ${params.includeSpouse ? 'left-4' : 'left-0.5'}`} />
                  </div>
                  {spouseOpen && params.includeSpouse
                    ? <ChevronUp size={13} />
                    : <ChevronDown size={13} />
                  }
                </div>
              </button>

              {/* Spouse fields */}
              {params.includeSpouse && (
                <div className="mt-3 p-3 bg-purple-950/30 border border-purple-700/30 rounded-xl">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-5 h-5 rounded-full bg-purple-600 flex items-center justify-center text-white text-xs font-bold">S</div>
                    <span className="text-xs font-semibold text-purple-400 uppercase tracking-wider">Spouse</span>
                    {/* Combined income note */}
                    <span className="ml-auto text-xs text-purple-400/60 italic">pooled portfolio</span>
                  </div>
                  <Slider accent="purple" label="Spouse Age" value={params.spouseAge} min={22} max={70} step={1} onChange={set('spouseAge')} />
                  <Slider accent="purple" label="Spouse Retirement Age" value={params.spouseRetirementAge} min={50} max={75} step={1} onChange={set('spouseRetirementAge')} />
                  <Slider accent="purple" label="Spouse Current Savings" value={params.spouseSavings} min={0} max={2000000} step={5000} onChange={set('spouseSavings')} format={fmt} fromAccounts />
                  <Slider accent="purple" label="Spouse Monthly Contrib" value={params.spouseContrib} min={0} max={10000} step={50} onChange={set('spouseContrib')} format={fmt} fromAccounts />
                  <Slider accent="purple" label="Spouse Social Security/mo" value={params.spouseSsMonthly} min={0} max={5000} step={50} onChange={set('spouseSsMonthly')} format={fmt} />
                  <Slider accent="purple" label="Spouse SS Claim Age" value={params.spouseSsAge} min={62} max={70} step={1} onChange={set('spouseSsAge')} />

                  {/* Age gap note */}
                  <div className="mt-2 text-xs text-purple-400/60 leading-snug bg-purple-900/20 rounded-lg p-2">
                    Spouse is age {params.spouseAge + (params.retirementAge - params.age)} when you retire at {params.retirementAge}.
                    {params.spouseRetirementAge > params.retirementAge - params.age + params.spouseAge
                      ? ` Spouse continues working ${params.spouseRetirementAge - (params.spouseAge + params.retirementAge - params.age)} more year(s) after your retirement, adding ${fmt(params.spouseContrib)}/mo to the portfolio.`
                      : ' Spouse retires before or at the same time as you.'}
                  </div>
                </div>
              )}
            </div>

            {/* ── Market assumptions ── */}
            <div className="mt-4 border-t border-slate-700 pt-4">
              <div className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3">Market Assumptions</div>
              <Slider label="Expected Return %" value={params.meanReturn} min={3} max={14} step={0.5} onChange={set('meanReturn')} format={v => `${v}%`} />
              <Slider label="Volatility (Std Dev) %" value={params.stdDev} min={5} max={25} step={0.5} onChange={set('stdDev')} format={v => `${v}%`} />
              <Slider label="Inflation %" value={params.inflation} min={1} max={6} step={0.25} onChange={set('inflation')} format={v => `${v}%`} />
            </div>

            <div className="mt-4 flex flex-col gap-2">
              <button onClick={runSim} disabled={running || !!multiRunning}
                className="w-full py-2.5 bg-blue-600 hover:bg-blue-500 disabled:bg-slate-700 disabled:cursor-not-allowed text-white rounded-lg text-sm font-semibold transition-all">
                {running ? 'Running…' : 'Run 500 Simulations'}
              </button>
              <button onClick={runMulti} disabled={running || !!multiRunning}
                className="w-full py-2.5 bg-slate-700 hover:bg-slate-600 disabled:opacity-50 disabled:cursor-not-allowed text-slate-200 border border-slate-600 rounded-lg text-sm font-semibold transition-all flex items-center justify-center gap-2">
                {multiRunning
                  ? <><span className="w-3 h-3 rounded-full border-2 border-slate-400 border-t-white animate-spin" />{`Running ${multiRunning}…`}</>
                  : '⚡ Run 500 / 1,000 / 5,000 Comparison'}
              </button>
            </div>
          </div>

          {/* ── Results ── */}
          <div className="lg:col-span-2 space-y-4">

            {/* Combined summary strip (only when spouse included) */}
            {params.includeSpouse && (
              <div className="grid grid-cols-3 gap-3">
                {[
                  { label: 'Combined Savings', value: fmt(combinedSavings), icon: '🏦' },
                  { label: 'Combined Contrib/mo', value: fmt(combinedContrib), icon: '💸' },
                  { label: 'Combined SS/mo', value: fmt(combinedSS), icon: '🏛' },
                ].map(k => (
                  <div key={k.label} className="bg-purple-950/30 border border-purple-700/30 rounded-xl p-3 text-center">
                    <div className="text-lg mb-0.5">{k.icon}</div>
                    <div className="text-sm font-bold text-purple-300">{k.value}</div>
                    <div className="text-xs text-purple-400/60 mt-0.5">{k.label}</div>
                  </div>
                ))}
              </div>
            )}

            {/* Success rate */}
            {result && (
              <div className="bg-slate-800 border border-slate-700 rounded-xl p-5">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <div className="text-xs text-slate-400 uppercase tracking-wider mb-1">
                      Probability of Success {params.includeSpouse && '· Household'}
                    </div>
                    <div className={`text-5xl font-black ${successColor(result.successRate)}`}>{result.successRate}%</div>
                    <div className="text-sm text-slate-400 mt-1">
                      {result.successRate >= 90 ? '✅ Excellent — your plan is robust'
                       : result.successRate >= 75 ? '⚠️ Good — consider small improvements'
                       : '❌ At risk — action needed'}
                    </div>
                  </div>
                  <div className="text-right space-y-1">
                    {[
                      { label: 'Best 10%', val: result.percentiles.p90 },
                      { label: 'Best 25%', val: result.percentiles.p75 },
                      { label: 'Median', val: result.percentiles.p50 },
                      { label: 'Worst 25%', val: result.percentiles.p25 },
                      { label: 'Worst 10%', val: result.percentiles.p10 },
                    ].map(p => (
                      <div key={p.label} className="flex items-center gap-3 justify-end">
                        <span className="text-xs text-slate-500">{p.label}</span>
                        <span className={`text-sm font-semibold ${p.val > 0 ? 'text-emerald-400' : 'text-rose-400'}`}>
                          {p.val > 0 ? fmtK(p.val) : 'Depleted'}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="w-full bg-slate-700 rounded-full h-2">
                  <div className={`h-2 rounded-full transition-all duration-500 ${successBg(result.successRate)}`} style={{ width: `${result.successRate}%` }} />
                </div>
                <div className="flex justify-between text-xs text-slate-600 mt-1">
                  <span>0%</span><span>Target: 85%</span><span>100%</span>
                </div>
              </div>
            )}

            {/* Canvas — view selector + chart */}
            <div className="bg-slate-800 border border-slate-700 rounded-xl p-5">
              <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                <h3 className="font-semibold text-slate-200">
                  Simulation Paths
                  {params.includeSpouse && <span className="text-xs text-purple-400 font-normal ml-1">(household combined)</span>}
                </h3>
                <div className="flex items-center gap-2">
                  {/* Canvas view tabs */}
                  {(['single', ...(multiResults ? ['500','1000','5000'] : [])]).map(v => (
                    <button key={v} onClick={() => setCanvasView(v)}
                      className={`px-2 py-1 rounded text-xs font-medium transition-all ${
                        canvasView === v ? 'bg-blue-600 text-white' : 'bg-slate-700 text-slate-400 hover:text-slate-200'
                      }`}>
                      {v === 'single' ? '500' : Number(v).toLocaleString()}
                    </button>
                  ))}
                  <div className="flex items-center gap-3 text-xs text-slate-500 ml-1">
                    <div className="flex items-center gap-1"><div className="w-3 h-1.5 bg-emerald-500 rounded" /><span>Survived</span></div>
                    <div className="flex items-center gap-1"><div className="w-3 h-1.5 bg-rose-500 rounded" /><span>Depleted</span></div>
                    <div className="flex items-center gap-1"><div className="w-4 bg-blue-400 rounded" style={{ height: 3 }} /><span>Median</span></div>
                  </div>
                </div>
              </div>
              {(() => {
                const displayResult =
                  canvasView === 'single' ? result :
                  canvasView === '500'    ? multiResults?.r500 :
                  canvasView === '1000'   ? multiResults?.r1000 :
                  canvasView === '5000'   ? multiResults?.r5000 : null;
                return displayResult
                  ? <MonteCarloCanvas result={displayResult} params={params} />
                  : <div className="h-[280px] flex items-center justify-center text-slate-500 text-sm">
                      {multiRunning === canvasView ? `Running ${canvasView} simulations…` : 'Run simulation to see paths'}
                    </div>;
              })()}
            </div>

            {/* ── Multi-run comparison ── */}
            {multiResults && (
              <div className="bg-slate-800 border border-slate-700 rounded-xl p-5">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="font-semibold text-slate-200">Simulation Comparison</h3>
                    <p className="text-xs text-slate-500 mt-0.5">More sims narrow the confidence interval — 5,000 gives the most stable estimate (±1–2%)</p>
                  </div>
                </div>

                {/* 3-column comparison */}
                <div className="grid grid-cols-3 gap-3 mb-4">
                  {[
                    { key: 'r500',  label: '500 sims',   time: 'fast',   color: 'border-slate-600' },
                    { key: 'r1000', label: '1,000 sims', time: 'better', color: 'border-blue-700/50' },
                    { key: 'r5000', label: '5,000 sims', time: 'best',   color: 'border-emerald-700/50' },
                  ].map(({ key, label, time, color }) => {
                    const r = multiResults[key];
                    const isLoading = !r && !!multiRunning;
                    return (
                      <div key={key} className={`rounded-xl border p-4 ${color} ${key === 'r5000' ? 'bg-emerald-950/20' : 'bg-slate-700/30'}`}>
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-xs font-semibold text-slate-300">{label}</span>
                          <span className={`text-xs px-1.5 py-0.5 rounded-full ${
                            time === 'best' ? 'bg-emerald-900/50 text-emerald-400' :
                            time === 'better' ? 'bg-blue-900/50 text-blue-400' :
                            'bg-slate-700 text-slate-400'
                          }`}>{time}</span>
                        </div>
                        {isLoading && !r ? (
                          <div className="flex items-center gap-2 py-4">
                            <span className="w-4 h-4 rounded-full border-2 border-slate-600 border-t-blue-400 animate-spin" />
                            <span className="text-xs text-slate-500">Running…</span>
                          </div>
                        ) : r ? (
                          <>
                            <div className={`text-3xl font-black mb-1 ${successColor(r.successRate)}`}>{r.successRate}%</div>
                            <div className="w-full bg-slate-700 rounded-full h-1.5 mb-3">
                              <div className={`h-1.5 rounded-full ${successBg(r.successRate)}`} style={{ width: `${r.successRate}%` }} />
                            </div>
                            <div className="space-y-1">
                              {[
                                { l: 'Best 10%',   v: r.percentiles.p90 },
                                { l: 'Best 25%',   v: r.percentiles.p75 },
                                { l: 'Median',     v: r.percentiles.p50 },
                                { l: 'Worst 25%',  v: r.percentiles.p25 },
                                { l: 'Worst 10%',  v: r.percentiles.p10 },
                              ].map(({ l, v }) => (
                                <div key={l} className="flex justify-between items-center">
                                  <span className="text-xs text-slate-500">{l}</span>
                                  <span className={`text-xs font-semibold ${v > 0 ? 'text-slate-300' : 'text-rose-400'}`}>
                                    {v > 0 ? fmtK(v) : 'Depleted'}
                                  </span>
                                </div>
                              ))}
                            </div>
                          </>
                        ) : null}
                      </div>
                    );
                  })}
                </div>

                {/* Convergence insight */}
                {multiResults.r500 && multiResults.r1000 && multiResults.r5000 && (() => {
                  const spread = Math.abs(multiResults.r5000.successRate - multiResults.r500.successRate);
                  const medDelta = Math.abs(multiResults.r5000.percentiles.p50 - multiResults.r500.percentiles.p50);
                  return (
                    <div className="grid grid-cols-3 gap-3 pt-4 border-t border-slate-700">
                      <div className="text-center">
                        <div className="text-xs text-slate-500 mb-1">Success rate spread</div>
                        <div className={`text-lg font-bold ${spread <= 3 ? 'text-emerald-400' : spread <= 6 ? 'text-amber-400' : 'text-rose-400'}`}>
                          ±{spread}%
                        </div>
                        <div className="text-xs text-slate-600 mt-0.5">{spread <= 3 ? 'tight — good convergence' : 'consider more sims'}</div>
                      </div>
                      <div className="text-center">
                        <div className="text-xs text-slate-500 mb-1">Median outcome delta</div>
                        <div className="text-lg font-bold text-slate-300">{fmtK(medDelta)}</div>
                        <div className="text-xs text-slate-600 mt-0.5">500 vs 5,000 sims</div>
                      </div>
                      <div className="text-center">
                        <div className="text-xs text-slate-500 mb-1">Recommended estimate</div>
                        <div className={`text-lg font-bold ${successColor(multiResults.r5000.successRate)}`}>{multiResults.r5000.successRate}%</div>
                        <div className="text-xs text-slate-600 mt-0.5">5,000-sim result</div>
                      </div>
                    </div>
                  );
                })()}
              </div>
            )}

            {/* Recommendations */}
            {result && (
              <div className="bg-slate-800 border border-slate-700 rounded-xl p-5">
                <div className="flex items-center gap-2 mb-4">
                  <Lightbulb size={16} className="text-amber-400" />
                  <h3 className="font-semibold text-slate-200">Recommendations</h3>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  {(tips || []).map((tip, i) => (
                    <div key={i} className="flex items-start gap-2 p-3 bg-slate-700/50 rounded-lg">
                      <TrendingUp size={14} className="text-emerald-400 flex-shrink-0 mt-0.5" />
                      <div>
                        <div className="text-emerald-400 font-bold text-xs">{tip.impact}</div>
                        <div className="text-sm font-medium text-slate-200 leading-tight">{tip.action}</div>
                        <div className="text-xs text-slate-400 mt-0.5">{tip.detail}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
